import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import confetti from 'canvas-confetti'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import AnimatedChecklist from './AnimatedChecklist'
import translations from './locales.json'
import useTranslation, {
  TranslationProvider,
} from '~/components/shared/TranslationProvider'
import { useIsMobile } from '~/hooks/useMediaQuery'

const TaskCompletedModal = ({ open = false, isExercise = false, locale }) => (
  <TranslationProvider
    translations={translations}
    scope="javascript.components.shared.task_completed_modal"
    locale={locale}
  >
    <TaskCompletedModalInner open={open} isExercise={isExercise} />
  </TranslationProvider>
)

const TaskCompletedModalInner = ({ open = false, isExercise = false }) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [dismissedTaskCompletedModal, setDismissedTaskCompletedModal] =
    useState(false)
  const isOpen = open && !dismissedTaskCompletedModal

  const handleClose = () => setDismissedTaskCompletedModal(true)

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const timer = window.setTimeout(() => {
      confetti({
        particleCount: 140,
        spread: 55,
        startVelocity: 42,
        angle: 90,
        origin: { x: isExercise ? 0.57 : 0.5, y: 0.82 },
        colors: ['#08467C', '#1C6F29', '#FBBF24', '#3B82F6'],
      })
    }, 250)

    return () => window.clearTimeout(timer)
  }, [isOpen, isExercise])

  if (!isOpen) {
    return null
  }

  const handlePrimaryAction = () => {
    handleClose()
    window.location.href = '/orientation/action_plan'
  }

  const handleOpenAutoFocus = () => {
    // Move focus to the action for quick keyboard access without trapping it.
    document.getElementById('task-completed-primary-action')?.focus()
  }

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      handleClose()
    }
  }

  const desktopModalPositionStyle = isExercise
    ? { left: '50%', transform: 'translateX(calc(-50% + 7.5rem))' }
    : { left: '50%', transform: 'translateX(-50%)' }

  const modalPositionStyle = isMobile
    ? { left: '50%', transform: 'translateX(-50%)' }
    : desktopModalPositionStyle

  return (
    <DialogPrimitive.Root
      open={isOpen}
      modal={false}
      onOpenChange={handleOpenChange}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          className="fixed bottom-4 z-[1080] w-[calc(100vw-2rem)] max-w-[450px] rounded-2xl border border-gray-200 bg-white p-0 shadow-[0_12px_32px_rgba(0,0,0,0.22)] outline-none sm:bottom-48"
          style={modalPositionStyle}
          onOpenAutoFocus={handleOpenAutoFocus}
        >
          <DialogPrimitive.Title className="sr-only">
            {t('title')}
          </DialogPrimitive.Title>

          <div className="relative flex items-start gap-3 px-4 py-4 sm:items-center sm:gap-4 sm:px-5">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center">
              <AnimatedChecklist />
            </div>

            <div className="min-w-0 flex-1 pr-7 text-left sm:pr-8">
              <p className="text-[14px] font-normal leading-5 text-[#1F2937]">
                {t('message')}
              </p>

              <button
                id="task-completed-primary-action"
                type="button"
                onClick={handlePrimaryAction}
                className="mt-1.5 text-[14px] font-semibold leading-5 text-intoo-blue-medium-800 transition-colors hover:text-intoo-blue-dark-900 hover:underline sm:mt-1"
              >
                {t('view_action_plan')}
              </button>
            </div>

            <DialogPrimitive.Close asChild>
              <button
                type="button"
                aria-label={t('close')}
                className="absolute right-4 top-4 flex h-5 w-5 items-center justify-center text-[#374151] transition-colors hover:text-[#111827]"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

export default TaskCompletedModal
